import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, ArrowLeft, KeyRound, ShieldCheck, Zap, BatteryCharging } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { REQUESTABLE_ROLES, ROLE_LABELS } from "@/lib/constants";
import api from "@/lib/apiClient";
import Loader from "@/components/Loader";

const LOGO = "/greenrgy-logo.jpg";

function fmtErr(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

function PwInput({ value, onChange, placeholder = "Password", testid }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input type={show ? "text" : "password"} value={value} onChange={onChange} placeholder={placeholder} className="pr-10" data-testid={testid} />
      <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid={`${testid}-toggle`}>
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

export default function Login() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [busy, setBusy] = useState(false);

  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  // register
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [reqRole, setReqRole] = useState("customer");
  // forgot
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");

  useEffect(() => { if (!loading && user) navigate("/dashboard"); }, [user, loading, navigate]);
  if (loading) return <Loader label="Checking session..." />;

  const google = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { identifier, password });
      setUser(data);
      toast.success(`Welcome back, ${data.name?.split(" ")[0] || ""}!`);
      navigate("/dashboard");
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", { name, email: email || null, phone: phone || null, password, role: reqRole });
      setUser(data);
      if (data.status === "pending") {
        toast.success("Account created! Your access is pending admin approval.");
      } else {
        toast.success("Account created!");
      }
      navigate("/dashboard");
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doForgotRequest = async () => {
    if (!identifier) { toast.error("Enter your email or phone first."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { identifier });
      if (data.demo_reset_token) {
        setResetToken(data.demo_reset_token);
        toast.success("Reset token generated (demo). Set your new password below.");
      } else {
        toast.info("If an account exists, a reset token was generated.");
      }
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token: resetToken, new_password: newPassword });
      toast.success("Password updated. Please log in.");
      setMode("login"); setPassword(""); setNewPassword(""); setResetToken("");
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 bg-background">
      {/* Brand side */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 text-white overflow-hidden" style={{ background: "var(--grad-side)" }}>
        <div className="absolute inset-0 grain-overlay opacity-40" />
        <div className="relative z-10 flex items-center gap-3">
          <img src={LOGO} alt="Greenrgy" className="h-12 w-12 rounded-xl object-contain bg-white p-1 ring-1 ring-white/20" />
          <div>
            <p className="font-display text-2xl font-bold leading-none">Greenrgy</p>
            <p className="text-[10px] uppercase tracking-[0.18em] text-white/50 mt-1" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>Warranty Console</p>
          </div>
        </div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="relative z-10">
          <h1 className="font-display text-4xl xl:text-5xl font-bold leading-tight">Lithium Battery<br />Warranty Portal</h1>
          <p className="mt-5 text-white/70 max-w-md">Register batteries, raise warranty claims and track resolutions across your entire dealer, distributor and service network — end to end.</p>
          <div className="mt-8 flex flex-wrap gap-3 text-sm">
            {[["Register", ShieldCheck], ["Claim", Zap], ["Resolve", BatteryCharging]].map(([t, I]) => (
              <span key={t} className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 ring-1 ring-white/15">
                <I className="h-4 w-4" style={{ color: "var(--c-lime)" }} /> {t}
              </span>
            ))}
          </div>
        </motion.div>
        <p className="relative z-10 text-xs text-white/40">© 2026 Greenrgy Energy Systems</p>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src={LOGO} alt="Greenrgy" className="h-11 w-11 rounded-xl object-contain bg-white ring-1 ring-border" />
            <span className="font-display text-2xl font-bold">Greenrgy</span>
          </div>

          <AnimatePresence mode="wait">
            {mode === "login" && (
              <motion.div key="login" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <p className="eyebrow">Welcome back</p>
                <h2 className="font-display text-3xl font-bold mt-1">Sign in</h2>
                <p className="mt-2 text-muted-foreground text-sm">Use your email or phone number and password.</p>

                <form onSubmit={doLogin} className="mt-6 space-y-4">
                  <div className="space-y-1.5">
                    <Label>Email or Phone</Label>
                    <Input value={identifier} onChange={(e) => setIdentifier(e.target.value)} placeholder="you@greenrgy.com or 98xxxxxxxx" data-testid="login-identifier" />
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label>Password</Label>
                      <button type="button" onClick={() => setMode("forgot")} className="text-xs font-medium text-primary hover:underline" data-testid="forgot-link">Forgot password?</button>
                    </div>
                    <PwInput value={password} onChange={(e) => setPassword(e.target.value)} testid="login-password" />
                  </div>
                  <Button type="submit" disabled={busy} className="w-full h-11 text-base" data-testid="login-submit">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
                  </Button>
                </form>

                <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground">
                  <div className="h-px flex-1 bg-border" /> OR <div className="h-px flex-1 bg-border" />
                </div>

                <Button onClick={google} variant="outline" className="w-full h-11 gap-3" data-testid="google-login-button">
                  <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z" /><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" /><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.5-4.6 2.4-7.2 2.4-5.2 0-9.6-3.3-11.2-8l-6.5 5C9.6 39.6 16.2 44 24 44z" /><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.6l6.2 5.2C41.4 35.6 44 30.3 44 24c0-1.3-.1-2.3-.4-3.5z" /></svg>
                  Continue with Google
                </Button>

                <p className="mt-6 text-center text-sm text-muted-foreground">
                  New here? <button onClick={() => setMode("register")} className="font-semibold text-primary hover:underline" data-testid="show-register">Create an account</button>
                </p>
              </motion.div>
            )}

            {mode === "register" && (
              <motion.div key="register" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <p className="eyebrow">Get started</p>
                <h2 className="font-display text-3xl font-bold mt-1">Create account</h2>
                <p className="mt-2 text-muted-foreground text-sm">Sign up and pick the role you need — an admin will review and approve your access.</p>
                <form onSubmit={doRegister} className="mt-6 space-y-4">
                  <div className="space-y-1.5"><Label>Full Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="reg-name" required /></div>
                  <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="optional if phone given" data-testid="reg-email" /></div>
                  <div className="space-y-1.5"><Label>Phone</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="optional if email given" data-testid="reg-phone" /></div>
                  <div className="space-y-1.5">
                    <Label>Role you're requesting</Label>
                    <Select value={reqRole} onValueChange={setReqRole}>
                      <SelectTrigger data-testid="reg-role"><SelectValue /></SelectTrigger>
                      <SelectContent>{REQUESTABLE_ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">Your requested role is confirmed by an admin during approval.</p>
                  </div>
                  <div className="space-y-1.5"><Label>Password</Label><PwInput value={password} onChange={(e) => setPassword(e.target.value)} testid="reg-password" /></div>
                  <Button type="submit" disabled={busy} className="w-full h-11" data-testid="register-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}</Button>
                </form>
                <button onClick={() => setMode("login")} className="mt-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-login"><ArrowLeft className="h-4 w-4" /> Back to sign in</button>
              </motion.div>
            )}

            {mode === "forgot" && (
              <motion.div key="forgot" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center mb-4"><KeyRound className="h-6 w-6 text-primary" /></div>
                <h2 className="font-display text-3xl font-bold">Reset password</h2>
                <p className="mt-2 text-muted-foreground text-sm">Enter your email or phone to get a reset token, then set a new password.</p>
                <div className="mt-6 space-y-4">
                  <div className="space-y-1.5">
                    <Label>Email or Phone</Label>
                    <div className="flex gap-2">
                      <Input value={identifier} onChange={(e) => setIdentifier(e.target.value)} data-testid="forgot-identifier" />
                      <Button type="button" variant="secondary" onClick={doForgotRequest} disabled={busy} data-testid="forgot-request">Get token</Button>
                    </div>
                  </div>
                  <form onSubmit={doReset} className="space-y-4">
                    <div className="space-y-1.5"><Label>Reset Token</Label><Input value={resetToken} onChange={(e) => setResetToken(e.target.value)} placeholder="paste token" data-testid="reset-token" /></div>
                    <div className="space-y-1.5"><Label>New Password</Label><PwInput value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="New password" testid="reset-newpassword" /></div>
                    <Button type="submit" disabled={busy || !resetToken} className="w-full h-11" data-testid="reset-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}</Button>
                  </form>
                </div>
                <button onClick={() => setMode("login")} className="mt-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-login-2"><ArrowLeft className="h-4 w-4" /> Back to sign in</button>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
}
